import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Download, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const CSVDocumentation: React.FC = () => {
  const { t } = useTranslation();
  const downloadSampleCSV = () => {
    const csvContent = `meter_id,apartment_number,building_name,first_name,last_name,email,phone
METER001,101,Building A,John,Doe,john.doe@example.com,+255123456789
METER002,102,Building A,Jane,Smith,jane.smith@example.com,
METER003,201,Building B,,,robert.j@example.com,+255111222333
METER004,202,Building B,Robert,Johnson,,`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_clients.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Accordion type="single" collapsible className="border rounded-lg">
      <AccordionItem value="documentation" className="border-none">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            <span className="font-semibold">{t('onboarding.csvDoc.title')}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2 text-foreground">{t('onboarding.csvDoc.requiredColumns')}</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">meter_id</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.meterIdDesc')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">apartment_number</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.apartmentDesc')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">building_name</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.buildingDesc')}</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-foreground">{t('onboarding.csvDoc.optionalColumns')}</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">first_name</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.firstNameDesc')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">last_name</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.lastNameDesc')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">email</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.emailDesc')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-muted px-2 py-1 rounded text-xs">phone</span>
                  <span className="text-muted-foreground">{t('onboarding.csvDoc.phoneDesc')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-foreground">{t('onboarding.csvDoc.exampleFormat')}</h4>
              <pre className="text-xs overflow-x-auto">
{`meter_id,apartment_number,building_name,first_name,last_name,email,phone
METER001,101,Building A,John,Doe,john.doe@example.com,+255123456789
METER002,102,Building A,Jane,Smith,jane.smith@example.com,
METER003,201,Building B,,,robert.j@example.com,+255111222333
METER004,202,Building B,Robert,Johnson,,`}
              </pre>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={downloadSampleCSV}
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('onboarding.csvDoc.downloadSample')}
            </Button>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>• {t('onboarding.csvDoc.noteBuildingMatch')}</p>
              <p>• {t('onboarding.csvDoc.noteMeterUnique')}</p>
              <p>• {t('onboarding.csvDoc.noteEmailUnique')}</p>
              <p>• {t('onboarding.csvDoc.noteEmailLogin')}</p>
              <p>• {t('onboarding.csvDoc.noteMaxSize')}</p>
              <p>• {t('onboarding.csvDoc.noteFormats')}</p>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
